import { Controller } from "@hotwired/stimulus"
import { PreviewMessageService } from "../services/preview_message_service"

// Manages the Remotion preview iframe in the compose editor.
// Sends assembled-video props to the preview player via PreviewMessageService.
export default class extends Controller {
  static targets = ["iframe", "status"]
  static values = { compositionId: String, props: Object, selectedBlock: Number }

  connect() {
    if (!this.hasIframeTarget) return

    this.previewService = new PreviewMessageService(this.iframeTarget)

    this.iframeTarget.addEventListener("load", () => {
      this.clearLoadTimeout()
      this.iframeLoaded = true
      // Small delay for React to mount inside the iframe
      setTimeout(() => {
        this.syncProps()
        this.seekToSelectedBlock()
      }, 300)
    }, { once: true })

    this.iframeTarget.addEventListener("error", () => {
      this.clearLoadTimeout()
      this.updateStatus("Preview unavailable")
    }, { once: true })

    // Timeout fallback if iframe never loads
    this.loadTimeout = setTimeout(() => {
      if (!this.iframeLoaded) {
        this.updateStatus("Preview unavailable")
      }
    }, 5000)
  }

  disconnect() {
    this.clearLoadTimeout()
    this.previewService = null
    this.iframeLoaded = false
  }

  clearLoadTimeout() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout)
      this.loadTimeout = null
    }
  }

  syncProps() {
    if (!this.iframeLoaded || !this.previewService) return

    this.previewService.setProps(this.propsValue)
    this.updateStatus("Preview ready")
  }

  // Called automatically by Stimulus when propsValue changes
  propsValueChanged() {
    if (this.iframeLoaded) {
      this.syncProps()
    }
  }

  // Called automatically by Stimulus when selectedBlockValue changes
  selectedBlockValueChanged() {
    if (this.iframeLoaded) {
      this.seekToSelectedBlock()
    }
  }

  seekToSelectedBlock() {
    if (!this.previewService) return

    const blocks = this.propsValue?.blocks
    if (!blocks || !blocks.length) return

    const index = this.selectedBlockValue
    if (index < 0 || index >= blocks.length) return

    const defaultTransition = this.propsValue?.defaults?.transition
    let frame = 0
    for (let i = 0; i < index; i++) {
      frame += blocks[i].durationFrames || 0
      // Subtract transition overlap (transitions run concurrently)
      const t = blocks[i].transition || defaultTransition
      if (t && t.type !== "none") {
        frame -= t.durationFrames ?? 15
      }
    }

    this.previewService.seekToFrame(Math.max(frame, 0))
  }

  updateStatus(text) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = text
    }
  }
}
